/**
 * DiagSurface: Diagnostics drawer -- surface diagnostics (ATM IV by tenor, validation, distributions), moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { badgeStyle, mutedText, secondaryText, ui } from "@components/screener/screenerUi";
import { pctFmt, titleizeBucket } from "@lib/screenerTruth";
import { toneFromAllocationValidation, topCountLabel } from "@lib/screenerViewHelpers";
import { type AllocationValidation, type AllocationValidationViolation, type ScreenerResponse, type SurfaceSummary } from "@lib/screenerTypes";

export type DiagSurfaceProps = {
  allocationValidation: AllocationValidation | null;
  allocationValidationViolations: AllocationValidationViolation[];
  candidateUniverseSummary: { total: number; selected: number; ranked: number; skipped: number; };
  data: ScreenerResponse;
  marketRankOnly: boolean;
  surfaceSummary: SurfaceSummary | undefined;
};

export default function DiagSurface({
  allocationValidation,
  allocationValidationViolations,
  candidateUniverseSummary,
  data,
  marketRankOnly,
  surfaceSummary,
}: DiagSurfaceProps) {
  return (
    <div className="rounded-2xl border p-3" style={{ borderColor: ui.cardBorder }}>
      <div className="mb-3 text-sm font-semibold">Surface diagnostics</div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <div className="text-xs font-semibold" style={secondaryText}>
            ATM IV by Tenor Bucket{" "}
            <span className="font-normal" style={mutedText}>
              (ORATS close {surfaceSummary?.market_surface_trade_date || "date not reported"})
            </span>
          </div>
          <div className="mt-2 space-y-1 text-sm">
            {surfaceSummary?.tenor_atm_iv &&
            Object.keys(surfaceSummary.tenor_atm_iv).length > 0 ? (
              Object.entries(surfaceSummary.tenor_atm_iv).map(([bucket, iv]) => (
                <div key={bucket} className="flex items-center justify-between">
                  <span style={mutedText}>{titleizeBucket(bucket)}</span>
                  <span className="font-semibold">
                    {pctFmt(typeof iv === "number" ? iv : null, 1)}
                  </span>
                </div>
              ))
            ) : (
              <div style={mutedText}>No tenor IV summary returned.</div>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold" style={secondaryText}>
            Screener Diagnostics
          </div>
          <div className="mt-2 space-y-1 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span style={mutedText}>{marketRankOnly ? "Market-rank validation" : "Allocation validation"}</span>
              <span style={badgeStyle(toneFromAllocationValidation(allocationValidation))}>
                {allocationValidation?.status ? String(allocationValidation.status).toUpperCase() : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span style={mutedText}>Invariant violations</span>
              <span className="font-semibold">
                {allocationValidationViolations.length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span style={mutedText}>Filtered count</span>
              <span className="font-semibold">
                {data.meta?.diagnostics?.filtered_count ?? "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span style={mutedText}>Ranked / Selected / Skipped</span>
              <span className="font-semibold">
                {`${candidateUniverseSummary.ranked || candidateUniverseSummary.total} / ${candidateUniverseSummary.selected} / ${candidateUniverseSummary.skipped}`}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span style={mutedText}>Fail-closed mode</span>
              <span className="font-semibold">
                {allocationValidation?.fail_closed == null ? "—" : allocationValidation.fail_closed ? "On" : "Off"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span style={mutedText}>Surface regime</span>
              <span className="font-semibold">
                {surfaceSummary?.surface_regime || "—"}
              </span>
            </div>
            {/* Shape/value DISTRIBUTIONS relocated here 2026-08-11 from the
                four small restatement cards. The dominant label alone can hide
                a near-tie, so the counts behind it stay on the page. */}
            <div className="flex items-center justify-between gap-3">
              <span style={mutedText}>Shape distribution</span>
              <span className="font-semibold">
                {topCountLabel(surfaceSummary?.candidate_shape_label_counts)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span style={mutedText} title="30m+ contracts are valued at the 3-year surface node (a proxy); see the own-tenor check below.">Dominant value (30m+ on 3y-node proxy)</span>
              <span className="font-semibold">
                {surfaceSummary?.candidate_value_label || "—"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span style={mutedText}>Value distribution</span>
              <span className="font-semibold">
                {topCountLabel(surfaceSummary?.candidate_value_label_counts)}
              </span>
            </div>
          </div>
        </div>

        {/* Drop Counts column removed 2026-08-10 — aggregate duplicate of the
            Filter Waterfall in the Screener Audit Trail card, which shows the
            same drops per stage with reasons. Do not cut the waterfall too. */}
      </div>
    </div>
  );
}
